// src/pages/Certificate.jsx — printable milestone certificate (/certificate).
// No PDF dependency: a print-optimized page + window.print() ("Save as PDF").
import React from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { HiArrowLeft, HiPrinter } from "react-icons/hi";
import logo from "../components/assets/images/logos-ravi.png";
import { useAuth } from "../context/AuthContext";
import { badgeById } from "../lib/gamification";
import { SEO } from "../components/common/SEO";

export default function Certificate() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { profile } = useAuth();

  const type = params.get("type") || "level";
  const value = params.get("value") || "";

  let headline = "Certificate of Achievement";
  let detail = "";
  let emblem = "🏆";
  if (type === "level") {
    detail = `for reaching Level ${value}`;
    emblem = "⭐";
  } else if (type === "badge") {
    const b = badgeById(value);
    detail = b ? `for earning the “${b.name}” badge` : "for an outstanding achievement";
    emblem = b?.icon || "🏅";
  } else {
    detail = value ? `for ${value}` : "for an outstanding achievement";
  }

  const name = profile?.handle || "Student";
  const dateStr = new Date().toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" });

  return (
    <div className="min-h-screen bg-slate-100 py-8 px-4">
      <SEO title="Certificate" description="Your achievement certificate." noIndex />

      {/* Print-only style: hide chrome, fit one page */}
      <style>{`
        @media print {
          body { background: #fff; }
          .no-print { display: none !important; }
          .cert-sheet { box-shadow: none !important; margin: 0 !important; }
          @page { size: landscape; margin: 12mm; }
        }
      `}</style>

      {/* Controls */}
      <div className="no-print max-w-3xl mx-auto flex items-center justify-between mb-5">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-1.5 text-gray-600 hover:text-indigo-600 font-semibold text-sm"
        >
          <HiArrowLeft /> Back
        </button>
        <button
          onClick={() => window.print()}
          className="inline-flex items-center gap-2 bg-indigo-600 text-white font-bold text-sm px-5 py-2.5 rounded-xl hover:bg-indigo-700 transition-colors shadow-md shadow-indigo-200"
        >
          <HiPrinter /> Print / Save as PDF
        </button>
      </div>

      {/* Certificate sheet */}
      <div className="cert-sheet max-w-3xl mx-auto bg-white rounded-2xl shadow-xl overflow-hidden">
        <div className="m-3 border-[6px] border-double border-indigo-200 rounded-xl p-8 sm:p-12 text-center relative">
          {/* Corner flourishes */}
          <div className="absolute top-3 left-3 w-10 h-10 border-t-4 border-l-4 border-amber-400 rounded-tl-lg" />
          <div className="absolute top-3 right-3 w-10 h-10 border-t-4 border-r-4 border-amber-400 rounded-tr-lg" />
          <div className="absolute bottom-3 left-3 w-10 h-10 border-b-4 border-l-4 border-amber-400 rounded-bl-lg" />
          <div className="absolute bottom-3 right-3 w-10 h-10 border-b-4 border-r-4 border-amber-400 rounded-br-lg" />

          <img src={logo} alt="Ravi TestPapers" className="h-14 mx-auto mb-4" />
          <p className="text-xs font-bold uppercase tracking-[0.3em] text-indigo-400 mb-6">{headline}</p>

          <div className="text-6xl mb-4">{emblem}</div>

          <p className="text-sm text-gray-400 mb-1">This certifies that</p>
          <h1 className="text-3xl sm:text-4xl font-black text-gray-800 mb-2">{name}</h1>
          <p className="text-base text-gray-500 mb-8">{detail}</p>

          <div className="flex items-center justify-center gap-10 mt-8 pt-6 border-t border-gray-100">
            <div className="text-center">
              <p className="text-lg font-black italic text-gray-700">Ravi TestPapers</p>
              <p className="text-[10px] uppercase tracking-widest text-gray-400 mt-1">Issued by</p>
            </div>
            <div className="text-center">
              <p className="text-sm font-bold text-gray-700">{dateStr}</p>
              <p className="text-[10px] uppercase tracking-widest text-gray-400 mt-1">Date</p>
            </div>
          </div>
        </div>
      </div>

      <p className="no-print text-center text-xs text-gray-400 mt-5">
        Tip: in the print dialog, choose “Save as PDF” to download.{" "}
        <Link to="/profile" className="text-indigo-500 font-semibold">Back to profile</Link>
      </p>
    </div>
  );
}
